import type { IntlNotification, NotificationsImpl } from "../utils/notifications";
import { Notifications } from "../utils/notifications";
import type { ServiceError } from "./BaseTypes";
import type { TrackingFunction } from "../utils/TrackingUtil";
import { trackStructured, trackUiError } from "../utils/TrackingUtil";
import { intlRoot } from "../IntlRootMixin";
export type ErrorCodeHandler = {
	canHandle: (arg0: ServiceError) => boolean;
	handle: (arg0: ServiceError, arg1: NotificationsImpl, arg2: TrackingFunction) => void;
};
export const cartErrorMessageHandlers: Array<ErrorCodeHandler> = [
	{
		canHandle: (error) => error.code === "OC-104",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_NOT_FOUND" }),
				id: "OC-104",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-105",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CREATE_CART" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-106",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SAVE_CART" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-107",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CART_EMPTY" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-108",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CREATE_CART" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-109",
		handle: (error, notifications) => {
			notifications.send({
				type: "warning",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_ARTICLE_NOT_FOUND" }, { article: error.value }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-110",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_STATUS_INVALID" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-111",
		handle: (error, notifications) => {
			notifications.send({
				type: "info",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_ITEM_NOT_BUYABLE" }, { article: error.value }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-112",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_ITEMS_WITH_QUANTITY_ERROR" }, { article: error.value }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-113",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_UNKNOWN" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-114" || error.code === "OC-116",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SERVICE_OPTION_TYPE_COLLISION" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-115",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SERVICE_OPTION_TYPE_MANDATORY_MISSING" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-117",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_BBD_COULD_NOT_BE_VALIDATED" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-118",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_BBD_INVALID_FOR_NONFOOD" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-119",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_BBD_NOT_APPROVED" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-122",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SOLR_UNAVAILABLE_NO_CUSTOMER" }),
				id: error.code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-124",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_REPLACEMENT_NOT_FOUND" }),
				id: "OC-124",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-125",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_TEMPORARY_NO_SERVICE" }),
				id: "OC-125",
			});
		},
	},
];

/**
 * Checkout errors, which should be handled by displaying
 * an error message.
 */
export const checkoutErrorMessageHandlers: Array<ErrorCodeHandler> = [
	{
		canHandle: (error) => error.code === "OC-210",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CHECKOUT_CART_INVALID" }),
				id: "OC-210",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-212",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_PRESELECTED_ADDRESS_REQUIRED" }),
				id: "OC-212",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-213",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CHECKOUT_NEEDS_PRICES" }),
				id: "OC-213",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-214",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_NO_PAYMENT_CONFIGURED" }),
				id: "OC-214",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-216",
		handle: (error, notifications, tracking) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_SHOPPINGCARTPAGE_WARN_CART_FULL" }),
				id: "OC-216",
			});
			tracking("checkout.error.tooManyItems");
		},
	},
	{
		canHandle: (error) => error.code === "OC-217",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_LIMIT_STOCK_QUANTITY" }),
				id: "OC-217",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-222",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_PICKUP_STORE_NOT_POSSIBLE_FOR_ARTICLES" }),
				id: "OC-222",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-223",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_PICKUP_DAY_TIMESLOT_INVALID" }),
				id: "OC-223",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-230",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_ADDRESS_NOT_FOUND" }),
				id: "OC-230",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-245",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_PAYMENT_NOT_ALLOWED" }, { paymentMethod: error.value }),
				id: "OC-245",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-250",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_COULD_NOT_LOAD_CUSTOMER_DATA" }),
				id: "OC-250",
			});
		},
	},
	{
		canHandle: ({ code }) => code === "OC-251",
		handle: ({ code }, { send }) => {
			send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CREDIT_CARD_INVALID_FOR_LOCATION_ID" }),
				id: code,
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-260",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_COULD_NOT_LOAD_ARTICLE_DATA" }),
				id: "OC-260",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-261",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CHECKOUT_ITEM_NOT_BUYABLE" }),
				id: "OC-261",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-280",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_PRICE_MISSING" }),
				id: "OC-280",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-290",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SUBMIT_FAILED" }),
				id: "OC-290",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-292",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CUSTOMER_BLOCKED" }, { customerId: error.value }),
				id: "OC-292",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-293",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SUBMIT_FAILED_DELIVERY_DATE" }),
				id: "OC-293",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-294",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_SUBMIT_FAILED_VALIDATION" }),
				id: "OC-294",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-295",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_UNSUPPORTED_FULFILLMENT" }, { articleIds: error.value }),
				id: "OC-295",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-297",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_CHECKOUT_WITH_NO_ACTIVE_CART" }),
				id: "OC-297",
			});
		},
	},
];
export const shoppingCartErrorMap: Map<string, IntlNotification> = new Map([
	[
		"OC-191",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_CARDHOLDER_BLOCKED" }),
			id: "oc-shoppingcart-cardholder-blocked",
		},
	],
	[
		"OC-192",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_SUBMIT_SAME_ORIGIN" }),
			id: "oc-shoppingcart-submit-same-origin",
		},
	],
	[
		"OC-403",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_NOT_MODIFIABLE" }),
			id: "oc-shopping-cart-not-modifiable",
		},
	],
	[
		"OC-404",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_NOT_FOUND" }),
			id: "oc-shopping-cart-not-found",
		},
	],
	[
		"OC-405",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_WRONG_STATE" }),
			id: "oc-shopping-cart-wrong-state",
		},
	],
	[
		"OC-406",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_UPDATE_INVALID" }),
			id: "oc-shopping-cart-update-invalid",
		},
	],
	[
		"OC-407",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_SUBMIT_INVALID" }),
			id: "oc-shopping-cart-submit-invalid",
		},
	],
	[
		"OC-409",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_VERSION_CONFLICT" }),
			id: "oc-shopping-cart-version-conflict",
		},
	],
	[
		"OC-433",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_MISSING_RIGHTS" }),
			id: "oc-shopping-cart-write-right-missing",
		},
	],
	[
		"OC-501",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_UPDATE_FAILED" }),
			id: "oc-shopping-cart-update-failed",
		},
	],
	[
		"OC-502",
		{
			type: "server-error",
			text: intlRoot.formatMessage({ id: "ORC_ERROR_SHOPPINGCART_SUBMIT_FAILED" }),
			id: "oc-shopping-cart-version-conflict",
		},
	],
]);
export const shoppingCartErrorHandlers: Array<ErrorCodeHandler> = [
	{
		canHandle: (e) => shoppingCartErrorMap.has(e.code),
		handle: (e, notifications) => {
			notifications.send(
				shoppingCartErrorMap.get(e.code) || {
					type: "server-error",
					text: intlRoot.formatMessage({ id: "ORC_ERROR_UNKNOWN" }),
					id: "OC-999",
				},
			);
		},
	},
];
export const generalErrorCodeHandlers: Array<ErrorCodeHandler> = [
	{
		canHandle: (e) => e.code === "OC-897",
		handle: (e, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_NETWORK" }),
				id: e.code,
			});
		},
	},
	{
		canHandle: (e) => e.code === "OC-929",
		handle: (e, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_RATE_LIMIT_REACHED" }),
				id: "oc-rate-limit-reached",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-940",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_UNKNOWN" }),
				id: "OC-940",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-943",
		handle: (error, notifications) => {
			notifications.send({
				type: "user-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_FORBIDDEN" }),
				id: "OC-943",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-950",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_UNKNOWN" }),
				id: "OC-950",
			});
		},
	},
	{
		canHandle: (error) => error.code === "OC-954",
		handle: (error, notifications) => {
			notifications.send({
				type: "server-error",
				text: intlRoot.formatMessage({ id: "ORC_ERROR_TIMEOUT" }),
				id: "OC-954",
			});
		},
	},
];
const fallbackErrorCodeHandler: ErrorCodeHandler = {
	canHandle: () => true,
	handle: (error) => {
		console.error("Unhandled error was handled by fallbackErrorCodeHandler.", error);
		trackUiError("ErrorHandler", error.code + error.message);
	},
};
export type ErrorHandlingStage = (next: ErrorHandlingStage | null | undefined, error: ServiceError) => void;
export class ErrorHandler {
	_specialErrorCodeHandlers: Array<ErrorCodeHandler>;

	constructor(errorCodeHandlers: Array<ErrorCodeHandler>) {
		this._specialErrorCodeHandlers = errorCodeHandlers;
	}

	_trackError(error: ServiceError, errorCodeHandler: ErrorCodeHandler) {
		const handlerName =
			errorCodeHandler === fallbackErrorCodeHandler
				? "fallback"
				: generalErrorCodeHandlers.indexOf(errorCodeHandler) > -1
					? "general"
					: "special";
		trackStructured("client.error", {
			tags: {
				code: error.code || "UNKNOWN",
				message: error.message,
				type: error.type || "",
				value: error.value || "",
				source: error.source || "",
				handlerName,
			},
		});
	}

	onError(next: ErrorHandlingStage | null | undefined, error: ServiceError) {
		const errorCodeHandler: ErrorCodeHandler =
			this._specialErrorCodeHandlers.find((handler) => handler.canHandle(error)) ||
			generalErrorCodeHandlers.find((handler) => handler.canHandle(error)) ||
			fallbackErrorCodeHandler;

		this._trackError(error, errorCodeHandler);

		errorCodeHandler.handle(error, Notifications, trackStructured);

		if (next) {
			next(null, error);
		}
	}
}
